import type {CitationTarget,FileRecord} from '@reados/contracts';
import {resolveReference} from '@reados/renderer/references';
import type {ReaderTab,TabState} from './workspace-context';

export interface ReaderNavigation {tab:ReaderTab;target:CitationTarget}

/** Targets and stored artifact paths are already decoded; preserve literal URL punctuation. */
function referenceForPath(path:string,files:FileRecord[],origin:string){
 return resolveReference(path.split('/').map(encodeURIComponent).join('/'),files,origin);
}

export function resolveReaderNavigation(target:CitationTarget,files:FileRecord[],origin:string):ReaderNavigation|undefined {
 if(target.fileId){
  const file=files.find(file=>file.id===target.fileId);
  if(!file)return undefined;
  if(target.contentRevision!==undefined&&(!/^[a-f0-9]{64}$/.test(target.contentRevision)||target.contentRevision!==file.contentRevision))return undefined;
  return {tab:{id:file.id,fileId:file.id,title:file.name,kind:file.kind},target:{...target,path:file.sandboxPath}};
 }
 const path=target.imagePath||target.path;
 if(!path)return undefined;
 const reference=referenceForPath(path,files,origin);
 if(!reference)return undefined;
 const resolved={...reference.target,...target,path:reference.target.path,...(reference.target.fileId?{fileId:reference.target.fileId}:{})};
 const file=reference.target.fileId?files.find(file=>file.id===reference.target.fileId):undefined;
 return {tab:file?{id:file.id,fileId:file.id,title:file.name,kind:file.kind}:{id:`artifact:${reference.target.path}`,artifactPath:reference.target.path,title:reference.label,kind:reference.kind},target:resolved};
}

/** Pinned readers use their freshly verified source metadata until ordinary navigation clears the target.
 * This projection never rewrites the library catalog or grants access from a cached path. */
export function readerReferenceFiles(readers:ReaderTab[],files:FileRecord[],targets:Record<string,CitationTarget>,verifiedSources:Record<string,FileRecord>):FileRecord[]{
 const pinned=new Map<string,FileRecord>();
 for(const tab of readers){
  if(!tab.fileId)continue;
  const source=verifiedSources[tab.fileId],revision=targets[tab.id]?.contentRevision;
  if(source&&source.id===tab.fileId&&revision&&source.contentRevision===revision)pinned.set(tab.fileId,source);
 }
 if(!pinned.size)return files;
 const result=files.map(file=>pinned.get(file.id)??file),present=new Set(files.map(file=>file.id));
 for(const [id,file] of pinned)if(!present.has(id))result.push(file);
 return result;
}

/** Repair old citation tabs only against the current owned catalog and shared parser. */
export function reconcileReaderReferences(readers:ReaderTab[],files:FileRecord[],origin:string):{readers:ReaderTab[];replacements:Map<string,string>;targets:Record<string,CitationTarget>} {
 const replacements=new Map<string,string>(),targets:Record<string,CitationTarget>={};
 const next:ReaderTab[]=[],seen=new Set<string>();let changed=false;
 for(const tab of readers){
  let replacement=tab;
  if(tab.fileId){
   const file=files.find(file=>file.id===tab.fileId);
   if(file&&(file.name!==tab.title||file.kind!==tab.kind))replacement={...tab,title:file.name,kind:file.kind};
  }else if(tab.artifactPath){
   const navigation=resolveReaderNavigation({path:tab.artifactPath},files,origin);
   if(navigation&&(navigation.tab.fileId||navigation.tab.artifactPath!==tab.artifactPath)){
    replacement=navigation.tab;
    targets[replacement.id]=navigation.target;
    if(replacement.id!==tab.id)replacements.set(tab.id,replacement.id);
   }
  }
  if(replacement!==tab)changed=true;
  if(seen.has(replacement.id)){changed=true;continue;}
  seen.add(replacement.id);next.push(replacement);
 }
 return {readers:changed?next:readers,replacements,targets};
}

export function remapReaderTabs(state:TabState,replacements:Map<string,string>):TabState {
 const remap=(id:string|null)=>id===null?null:replacements.get(id)??id;
 const ids=[...new Set(state.ids.map(id=>remap(id)!))],active=remap(state.active),secondary=remap(state.secondary);
 return {...state,ids,active,secondary:secondary===active?null:secondary};
}
